import { useState } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { X } from 'lucide-react';

/**
 * Chip-style input for editing a list of carer names. Replaces the previous
 * comma-separated text input which silently stripped trailing spaces and
 * tripped users up when typing names with spaces.
 *
 * - Press Enter or click "Add" to commit a name.
 * - Click the X on a chip to remove that name.
 */
export const CarerNamesInput = ({ value = [], onChange, placeholder = 'Add carer name', testId = 'carer-names' }) => {
  const [draft, setDraft] = useState('');

  const commit = () => {
    const name = draft.trim();
    if (!name) return;
    if (value.includes(name)) {
      setDraft('');
      return;
    }
    onChange([...value, name]);
    setDraft('');
  };

  const remove = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKey}
          placeholder={placeholder}
          className="flex-1"
          data-testid={`${testId}-input`}
        />
        <Button
          type="button"
          variant="secondary"
          onClick={commit}
          disabled={!draft.trim()}
          data-testid={`${testId}-add`}
        >
          Add
        </Button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid={`${testId}-chips`}>
          {value.map((name, i) => (
            <span
              key={`${name}-${i}`}
              className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-inset text-ink-70 text-sm"
            >
              {name}
              <button
                type="button"
                onClick={() => remove(i)}
                className="rounded-full hover:bg-rule p-0.5 transition-colors duration-state"
                aria-label={`Remove ${name}`}
                data-testid={`${testId}-chip-remove-${i}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default CarerNamesInput;
